/**
 * WritingPracticeHeroCard — the "Show, don't tell" writing-coach doorway card.
 *
 * Implements show-not-tell/TS-003 (T4, AC-TS003.2). Occupies the student
 * dashboard hero's RIGHT slot in place of PebblesHeroCard when
 * dashboard.features.show_not_tell is true (wired by StudentDashboardPage,
 * T5). Shows a NEW tag, the title "Show, don't tell", a one-line hook, a
 * before -> after (telling vs showing) example, and a primary CTA.
 *
 * Reuses the rounded-3xl gradient card scaffold cloned from PebblesHeroCard
 * and the shared Button component (variant="warm" size="lg" h-12 = 48px,
 * satisfying the >=44px tap-target requirement).
 *
 * Pure presentational — no data fetch, no queryClient.invalidateQueries call.
 * Enforces TO-LL-026: the CTA ONLY navigates; it must never itself start a
 * session or invalidate/refetch queries on click. Re-entitlement at the
 * destination is re-checked by FeatureGuard (TS-004), not here.
 *
 * Modified by show-not-tell/US-003 (T4, AC-003.2): the button navigates via
 * a centralized ROUTES constant instead of a path literal, enforcing
 * TO-LL-013 (no hardcoded navigate paths).
 *
 * Modified by BUG-20260718-154634 (C-1): retargets the CTA from
 * ROUTES.WRITING_PRACTICE (which jumped straight into starting/resuming a
 * session, with no listing stop) to ROUTES.WRITING_PRACTICE_HISTORY — the
 * writing-sessions LANDING page (upgraded by C-2 into a proper
 * start/resume/history landing, mirroring comprehension's SessionsListPage).
 * Relabelled "Go to my writing sessions" to match comprehension's dashboard
 * CTA convention ("Go to my sessions", StudentDashboardPage.tsx) per
 * operator confirmation in the ticket. Still navigation-only (TO-LL-026):
 * this button never starts a session itself — the landing page owns that.
 */

import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/constants/routes';

export function WritingPracticeHeroCard() {
  const navigate = useNavigate();

  return (
    <div className="relative flex h-full flex-col overflow-hidden rounded-3xl border border-sky-200 bg-gradient-to-br from-sky-50 via-cyan-50 to-teal-100 p-6 shadow-lg">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full bg-teal-400/20 blur-2xl"
      />

      {/* NEW badge */}
      <span className="relative z-[1] mb-3 inline-flex w-fit items-center rounded-full bg-otter-orange-600 px-2 py-0.5 text-[11px] font-extrabold uppercase tracking-wider text-white">
        New
      </span>

      <h3 className="relative z-[1] font-display text-xl font-semibold leading-tight text-otter-earth-800">
        Show, don&apos;t tell
      </h3>
      <p className="relative z-[1] mt-1 text-[13px] font-semibold text-otter-earth-700">
        Turn telling sentences into vivid showing ones.
      </p>

      {/* Before -> after example */}
      <div className="relative z-[1] mt-4 rounded-2xl border border-otter-sand bg-white/80 p-3">
        <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Telling</p>
        <p className="mt-0.5 text-sm text-otter-earth-800">She was sad.</p>
        <p aria-hidden="true" className="my-2 text-center text-sm text-gray-400">
          &darr;
        </p>
        <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Showing</p>
        <p className="mt-0.5 text-sm text-otter-earth-800">
          Her shoulders slumped, and tears blurred her eyes.
        </p>
      </div>

      <div className="relative z-[1] mt-auto pt-5">
        <Button
          variant="warm"
          size="lg"
          className="h-12 w-full"
          aria-label="Go to my writing sessions"
          onClick={() => navigate(ROUTES.WRITING_PRACTICE_HISTORY)}
        >
          Go to my writing sessions
        </Button>
      </div>
    </div>
  );
}
